'use client';
import { DateTime } from 'luxon';
import React, { useState } from 'react';

import { BlockWrapper } from '../../../../components/block-wrapper/block-wrapper';
import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { PageComponentImage } from '../../../../components/page-components/image/page-component-image';
import ShowMore from '../../../../components/page-components/list-page/show-more';
import { PageComponentMainBottomBorder } from '../../../../components/page-components/main/page-component-main-bottom-border';
import { PageComponentMainHeader } from '../../../../components/page-components/main/page-component-main-header';
import { Share } from '../../../../components/share/share';
// import { FavouriteButton } from '@/components/ui/favourite-button/favourite-button'; // TODO: re-enable when LPO favourites are needed
import LinkButton from '../../../../components/ui/link-button/link-button';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { useIsMobile } from '../../../../hooks/use-is-mobile';
import { IMeetingplacePageHomeMinified } from '@/lib/fragments/meetingPlacePageHome-minified';
import { ILibraryMeetingPlace } from '@/lib/fragments/libraryMeetingPlace';
import { IMeetingPlacePageLeisurePackageOfferItem } from '@/lib/fragments/meetingPlacePageLeisurePackageOfferItem';
import TenantDescription from '@/components/page-components/tenant-detail-page/tenant-description';
import { LeisurePackageOfferCard } from '@/components/leisure-package-offer-card/leisure-package-offer-card';
import BuyNow from '@/components/leisure-package-offer/buy-now/buy-now';
import { useSearchParams } from 'next/navigation';
import { IGtmParameters, useSendGtmEvent } from '@/hooks/use-send-gtm-event';
import { GtmPageView } from '@/components/page-components/analytics/gtm-page-view';
import {
  useGetLibraryConstantsDictionaryData,
  useIsMPLivliFamily,
} from '@/providers/ConfigProvider';
import { useUser } from '@/providers/UserProvider';

interface MeetingPlacePageLeisurePackageOfferProps {
  metaData: any;
  node_locale: string;
  parentSlug: string;
  lpoData: IMeetingPlacePageLeisurePackageOfferItem | null;
  homeData: IMeetingplacePageHomeMinified | null;
  allLPOList: ILibraryMeetingPlace | null;
  parent?: any;
  stripeEnabled?: boolean;
}

export const MeetingPlacePageLeisurePackageOffer = ({
  metaData,
  node_locale,
  parentSlug,
  lpoData,
  homeData,
  allLPOList,
  parent,
  stripeEnabled = false,
}: MeetingPlacePageLeisurePackageOfferProps) => {
  const { user } = useUser();
  const isLivliFamily = useIsMPLivliFamily();
  const page = lpoData;
  const homepage = homeData;
  const dictionary = useGetLibraryConstantsDictionaryData();
  const image = page?.coverImage ? page?.coverImage : undefined;
  const pageName = page?.name ?? '';

  const isMobile = useIsMobile();
  const isWebView = useGetMobileAppWebViewHeader();

  const searchParams = useSearchParams();
  const [showCancelled, setShowCancelled] = useState(
    searchParams.get('payment') === 'cancelled',
  );

  const otherOffers =
    allLPOList?.leisurePackageOfferListPage?.leisurePackageOfferItemsCollection?.items
      ?.filter(Boolean)
      ?.filter((offer: any) => offer?.sys.id !== page?.sys?.id)
      ?.filter((offer: any) => {
        return (
          !offer.offerValidTill ||
          DateTime.fromISO(offer.offerValidTill).diffNow('day').days > 0
        );
      }); //Filter out those offers which offerValidTill is before today

  const [currPage, setCurrPage] = useState(1);
  const _offersListSize = 4;
  const slicedList = otherOffers?.slice(0, _offersListSize * currPage);

  const gtmParams: IGtmParameters = {
    event: 'view_leisure_package_offer_detail',
    detail_name: pageName,
    contentful_id: page?.sys?.id || '',
    offer_type: 'Offer Type Not Available',
    user_status: isLivliFamily ? (user ? 'logged_in' : 'guest') : undefined,
  };
  useSendGtmEvent(gtmParams);

  return (
    <>
      {/* ✅ GTM PAGE VIEW */}
      <GtmPageView
        homepage={homepage}
        language={node_locale}
        pageTitle={page?.name ?? ''}
      />
      {showCancelled && (
        <div
          className="mx-6 mt-4 flex items-center justify-between rounded-lg bg-background-secondary px-4 py-3 text-bodys text-text-default lg:mx-0"
          role="status"
        >
          <span>
            {(dictionary as { paymentCancelled?: string })?.paymentCancelled ??
              'Payment cancelled.'}
          </span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setShowCancelled(false)}
            className="ml-4 font-bold"
          >
            ×
          </button>
        </div>
      )}
      {isMobile ? (
        <>
          <div className="w-full rounded-b-xxl bg-background-primary-light px-6 pb-8">
            <Breadcrumbs
              breadcrumbs={[pageName]}
              homeText={homepage?.breadcrumbHomeText ?? ''}
              cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
              node_locale={node_locale}
              parent={parent}
            />
            <div className="col-span-12 bg-background-primary-light  pt-5">
              <PageComponentImage image={image} node_locale={node_locale} />
            </div>
            <div className="mt-6">
              {/* TODO: re-enable when LPO favourites are needed
              <div className="flex items-center justify-between">
                <PageComponentMainHeader header={pageName} />
                <FavouriteButton id={page?.sys?.id ?? ''} type="leisurePackageOffer" />
              </div>
              */}
              <PageComponentMainHeader header={pageName} />
              <div className="flex flex-wrap gap-2 mt-1">
                {page?.leisurePackageOfferTagsCollection &&
                  page?.leisurePackageOfferTagsCollection?.items
                    .filter(Boolean)
                    ?.map((tag, key) => (
                      <span
                        key={key}
                        className="flex-shrink-0 rounded-[4px] border-2 border-neutral-grey-200 bg-background-primary-light px-2 text-bodyxs font-bold text-text-default"
                      >
                        {tag?.name}
                      </span>
                    ))}
              </div>
              {page?.viewPackageDetails && (
                <LinkButton
                  to={page.viewPackageDetails.asset.url}
                  className="mt-4 w-full text-center"
                  openInNewTab
                  isExternalLink
                  variant="secondary"
                >
                  {page.viewPackageDetails.buttonText}
                </LinkButton>
              )}
              {page && (
                <BuyNow
                  offer={page}
                  nodeLocale={node_locale}
                  parentSlug={parentSlug}
                  stripeEnabled={stripeEnabled}
                  mpId={homepage?.meetingPlace?.businessUnitCode}
                />
              )}
            </div>
          </div>
          <div className="mt-8 px-6">
            {page?.description && (
              <TenantDescription
                description={page?.description}
                node_locale={node_locale}
              />
            )}
            {homepage?.meetingPlace.showSocialShare && (
              <Share className="ml-auto" />
            )}
            <div className="lg:mt-6lg:p-0 -mx-6 bg-brand-white px-6 pt-8 lg:m-0 lg:pt-0">
              <div className="mb:0 grid grid-cols-1 gap-5 lg:mb-12">
                {slicedList?.map((offer: any, index: any) => (
                  <LeisurePackageOfferCard
                    card={offer}
                    cardIndex={index}
                    node_locale={node_locale}
                    key={offer?.sys?.id}
                    dictionary={dictionary}
                  />
                ))}
              </div>
              {otherOffers && otherOffers.length > _offersListSize && (
                <ShowMore
                  currentPage={currPage}
                  maxNum={otherOffers.length}
                  setPage={setCurrPage}
                  pageSize={_offersListSize}
                  ofString={dictionary?.of}
                  showMoreString={dictionary?.showMore}
                  showingString={dictionary?.showing}
                />
              )}
            </div>
          </div>
        </>
      ) : (
        <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
          <Breadcrumbs
            breadcrumbs={[pageName]}
            homeText={homepage?.breadcrumbHomeText ?? ''}
            cssClass="lg:ml-22"
            node_locale={node_locale}
            parent={parent}
          />
          <div className="grid grid-cols-11 gap-6 pt-5">
            <div className="col-span-12 bg-background-primary-light lg:col-span-5 lg:bg-transparent">
              <PageComponentImage image={image} node_locale={node_locale} />
            </div>
            <div className="col-span-12 lg:col-span-6">
              <BlockWrapper>
                {/* TODO: re-enable when LPO favourites are needed
                <div className="flex items-center justify-between mb-4">
                  <PageComponentMainHeader header={pageName} />
                  <FavouriteButton id={page?.sys?.id ?? ''} type="leisurePackageOffer" />
                </div>
                */}
                <div className="mb-4">
                  <PageComponentMainHeader header={pageName} />
                </div>
                <div className="flex flex-wrap gap-2 mt-1">
                  {page?.leisurePackageOfferTagsCollection &&
                    page?.leisurePackageOfferTagsCollection?.items
                      .filter(Boolean)
                      ?.map((tag, key) => (
                        <span
                          key={key}
                          className="flex-shrink-0 rounded-[4px] border-2 border-neutral-grey-200 bg-background-primary-light px-2 text-bodyxs font-bold text-text-default"
                        >
                          {tag?.name}
                        </span>
                      ))}
                </div>
                <PageComponentMainBottomBorder />
                {page?.viewPackageDetails && (
                  <LinkButton
                    to={page?.viewPackageDetails.asset.url}
                    className="mt-4 text-center text-bodys font-bold"
                    openInNewTab
                    isExternalLink
                    variant="secondary"
                  >
                    {page.viewPackageDetails.buttonText}
                  </LinkButton>
                )}

                {page && (
                  <BuyNow
                    offer={page}
                    nodeLocale={node_locale}
                    parentSlug={parentSlug}
                    stripeEnabled={stripeEnabled}
                    mpId={homepage?.meetingPlace?.businessUnitCode}
                  />
                )}

                {page?.description && (
                  <TenantDescription
                    description={page.description}
                    node_locale={node_locale}
                  />
                )}

                {homepage?.meetingPlace.showSocialShare && (
                  <Share className="absolute -bottom-2 right-0 lg:bottom-6 lg:right-20 xl:right-24" />
                )}
              </BlockWrapper>
              <div className="-mx-6 mt-10 bg-brand-white px-6 pt-8 lg:m-0 lg:p-0 lg:pt-0">
                <div className="mb:0 mt-6 grid grid-cols-2 gap-5 lg:mb-12">
                  {slicedList?.map((offer: any, index: any) => (
                    <LeisurePackageOfferCard
                      card={offer}
                      cardIndex={index}
                      node_locale={node_locale}
                      key={offer?.sys?.id}
                      dictionary={dictionary}
                      parentSlug={parentSlug}
                    />
                  ))}
                </div>
              </div>
              {otherOffers && otherOffers.length > _offersListSize && (
                <ShowMore
                  currentPage={currPage}
                  maxNum={otherOffers.length}
                  setPage={setCurrPage}
                  pageSize={_offersListSize}
                  ofString={dictionary?.of}
                  showMoreString={dictionary?.showMore}
                  showingString={dictionary?.showing}
                />
              )}
            </div>
          </div>
        </main>
      )}
    </>
  );
};

export default MeetingPlacePageLeisurePackageOffer;
